import { axiosGet } from './axiosGet';
import { axiosPost } from './axiosPost';
import { axiosPut } from './axiosPut';
import { devLog } from './devLog';

function handleNominationUpdate(
  nominationId,
  draftFormData,
  userDetails,
  refreshNominations,
  setRefreshNominations,
) {
  if (nominationId && typeof nominationId === 'number') {
    axiosGet(`nominations/${nominationId}`).then((nominationsResponse) => {
      if (nominationsResponse) {
        axiosPut(`nominations/${nominationId}`, {
          data: {
            value: draftFormData,
          },
        }).then((nominationUpdate) => {
          if (nominationUpdate && nominationUpdate?.status === 200) {
            setTimeout(() => {
              if (userDetails?.roles?.includes('admin')) {
                setRefreshNominations(!refreshNominations);
              }
            }, 2000);
          }
        });
      }
    });
  }
}

export const saveNominationDraft = (
  formValues,
  refreshCollections,
  setRefreshCollections,
  userDetails,
  nominationId,
  refreshNominations,
  setRefreshNominations,
  usersArray,
  errors,
  numberOfErrors,
  setNominationId,
) => {
  if (!formValues) return;
  const collectionName = 'nomination-drafts';
  const draftFormData = JSON.stringify(formValues);
  const proposerFellowId = formValues?.proposerFellowId?.value;
  const proposerUser = usersArray.find(
    (user) => user?.fellowId === proposerFellowId,
  );

  const proposerUserId = proposerUser?.id ?? userDetails?.id;
  const { name, lastName } = formValues;

  const blockingErrorTypes = [
    'expired-nomination',
    'already-elected',
    'deceased-candidate',
    'active-nomination-duplicate',
    'draft-duplicate',
  ];

  const hasBlockingErrors = [
    errors?.name?.type,
    errors?.lastName?.type,
    errors?.email?.type,
  ].some((errorType) => blockingErrorTypes.includes(errorType));

  if (
    !name ||
    !lastName ||
    name.length === 0 ||
    lastName.length === 0 ||
    hasBlockingErrors
  ) {
    return;
  }

  const nominationDraftData = {
    data: {
      userId: proposerUserId,
      name,
      lastName,
      value: draftFormData,
    },
  };

  const handleDraftSaveResponse = (apiCallResponse) => {
    if (!apiCallResponse?.data?.data) return;

    setRefreshCollections(!refreshCollections);

    if (userDetails?.roles?.includes('admin')) {
      setRefreshNominations(!refreshNominations);
    }

    const newDraftId = apiCallResponse.data.data.id;

    if (setNominationId) {
      setNominationId(`${newDraftId}d`);
    }

    handleNominationUpdate(
      nominationId,
      draftFormData,
      userDetails,
      refreshNominations,
      setRefreshNominations,
    );
  };

  // If the wizard already has a draft id in context, update that draft directly.
  // Draft ids are tagged with a trailing `d` (set by this function after a save
  // and by Nomination.jsx / AdminNomination.jsx when building the draft list).
  // Without this short-circuit a rename would miss the name-based lookup below
  // and spawn a duplicate draft row.
  const extractDraftId = (value) => {
    if (typeof value !== 'string') return null;
    const match = value.match(/^(\d+)d$/);
    return match ? match[1] : null;
  };
  const knownDraftId = extractDraftId(nominationId);

  if (knownDraftId) {
    axiosPut(`${collectionName}/${knownDraftId}`, nominationDraftData).then(
      handleDraftSaveResponse,
    );
    return;
  }

  // Candidate-lookup filters must be URL-encoded — names with apostrophes,
  // accents, ampersands or spaces otherwise break the Strapi filter query and
  // cause the lookup to miss, spawning a duplicate draft.
  const encodedName = encodeURIComponent(name);
  const encodedLastName = encodeURIComponent(lastName);

  if (proposerUserId !== userDetails?.id) {
    axiosGet(
      `${collectionName}?filters[userId][$eq]=${userDetails?.id}&filters[name][$eq]=${encodedName}&filters[lastName][$eq]=${encodedLastName}`,
    ).then((nominationDraftResponse) => {
      if (nominationDraftResponse?.data?.data?.length !== 0) {
        const draftId = nominationDraftResponse.data.data[0].id;

        axiosPut(`${collectionName}/${draftId}`, {
          data: {
            publishedAt: null,
          },
        }).then((updateResponse) => {
          if (updateResponse.status === 200) {
            devLog('Draft nomination saved:', updateResponse);
          }
        });
      }
    });
  }

  axiosGet(
    `${collectionName}?filters[userId][$eq]=${proposerUserId}&filters[name][$eq]=${encodedName}&filters[lastName][$eq]=${encodedLastName}`,
  ).then((draftResponse) => {
    const draftResponseId = draftResponse?.data?.data?.[0]?.id;

    let apiCall;

    if (draftResponseId) {
      apiCall = axiosPut(
        `${collectionName}/${draftResponseId}`,
        nominationDraftData,
      );
    } else if (numberOfErrors > 0) {
      apiCall = axiosPost(`${collectionName}`, nominationDraftData);
    }

    if (apiCall) {
      apiCall.then(handleDraftSaveResponse);
    }
  });
};
